import type { ILesson } from '@/types/lesson';

import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useRef, useMemo, Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  IconBan,
  IconEye,
  IconTrash,
  IconSearch,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomImage from '@/components/CustomImage';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import ListSkeleton from '@/components/SkeletonLoader/ListSkeleton';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { EBlockUnblock, ELessonStatus, ERestoreEntityType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { removeLesson, getLessonList } from '@/service/lessonService';
import { blockUnblockUser } from '@/service/teacherService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, formatLastActive, formatUnixToLongDate } from '@/utils';

import FilterDrawer, { type ILessonFilters } from './components/FilterDrawer';

const ACTIONS_BLOCK = 'common.actions.block';
const ACTIONS_UNBLOCK = 'common.actions.unblock';

// ── Helpers (module-level, no complexity cost inside the component) ────────

const applyFiltersToQuery = (
  query: Record<string, unknown>,
  filters: ILessonFilters
) => {
  if (filters.status) query.status = filters.status;
  if (filters.classAssigned) {
    query.class_ids = Array.isArray(filters.classAssigned)
      ? filters.classAssigned
      : [filters.classAssigned];
  }
};

const buildLessonQuery = (
  currentPage: number,
  debouncedSearch: string,
  sortField: string,
  sortDir: string,
  filters: ILessonFilters | null
): Record<string, unknown> => {
  const query: Record<string, unknown> = {
    limit: 10,
    offset: (currentPage - 1) * 10,
  };

  if (debouncedSearch) query.search = debouncedSearch;

  if (sortField && sortDir) {
    query[`order[${sortField}]`] = sortDir;
  } else {
    query[`order[created_at]`] = 'DESC';
  }

  if (filters) applyFiltersToQuery(query, filters);

  return query;
};

const Lessons = () => {
  const { formatText } = useFormatText();
  const navigate = useNavigate();
  const { setTitle } = usePageTitle();
  const language = useSettingsStore((state) => state.settings.language);
  const pageTitle = formatText('lessons.title');

  useEffect(() => {
    setTitle(pageTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Form for search input
  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  const [lessons, setLessons] = useState<ILesson[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();
  const isInitialLoad = useRef(true);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('DESC');

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    lesson: ILesson | null;
  }>({ open: false, lesson: null });

  const [blockModal, setBlockModal] = useState<{
    open: boolean;
    lesson: ILesson | null;
  }>({ open: false, lesson: null });

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [filters, setFilters] = useState<ILessonFilters | null>(null);

  const isFilterApplied = useMemo(() => {
    if (!filters) return false;
    const hasClassFilter = Array.isArray(filters.classAssigned)
      ? filters.classAssigned.length > 0
      : !!filters.classAssigned;

    const hasStatusFilter = !!filters.status;
    return hasClassFilter || hasStatusFilter;
  }, [filters]);

  // ── Fetch lessons ────────────────────────────────────────────────────────

  const setLoadingState = (loading: boolean) => {
    if (isInitialLoad.current) {
      setScreenLoading(loading);
    } else {
      setTableLoading(loading);
    }
  };

  const fetchLessons = async () => {
    setLoadingState(true);
    try {
      const query = buildLessonQuery(
        currentPage,
        debouncedSearch,
        sortField,
        sortDir,
        filters
      );
      const res = await getLessonList(query);
      if (res?.data) {
        setLessons(res.data ?? []);
        setTotal(res.total ?? res.data.length ?? 0);
      }
    } finally {
      setLoadingState(false);
      if (isInitialLoad.current) isInitialLoad.current = false;
    }
  };

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, sortField, sortDir, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSort = (key: string) => {
    if (sortField !== key) {
      setSortField(key);
      setSortDir('ASC');
      return;
    }

    if (sortDir === 'ASC') {
      setSortDir('DESC');
    } else if (sortDir === 'DESC') {
      setSortField('');
      setSortDir('');
    } else {
      setSortDir('ASC');
    }
  };

  const handleRemove = async () => {
    if (!confirmModal.lesson) return;
    try {
      const response = await removeLesson(
        confirmModal.lesson.id,
        ERestoreEntityType.LESSON
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setConfirmModal({ open: false, lesson: null });
        fetchLessons();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleBlockLesson = async () => {
    if (!blockModal.lesson) return;
    const isCurrentlyInactive =
      blockModal.lesson.status === ELessonStatus.IN_ACTIVE;

    try {
      const response = await blockUnblockUser(
        blockModal.lesson.id,
        EBlockUnblock.LESSON,
        { is_block: !isCurrentlyInactive }
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setBlockModal({ open: false, lesson: null });
        fetchLessons();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleApplyFilters = (newFilters: ILessonFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // ── Columns ──────────────────────────────────────────────────────────────

  const columns = useMemo(
    () => [
      {
        key: 'lesson_name',
        title: 'lessons.columns.lesson-name',
        sorter: true,
        handleSort: () => handleSort('lesson_name'),
        width: '250px',
        render: (record: ILesson) => (
          <div className="flex items-center gap-3">
            <CustomImage
              src={'/assets/images/lesson.jpg'}
              alt={record.lesson_name}
              className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center text-xl shrink-0"
            />
            <span className="text-neutral-primary font-medium text-sm truncate max-w-40">
              {record.lesson_name}
            </span>
          </div>
        ),
      },
      {
        key: 'created_by',
        title: 'lessons.columns.created-by',
        sorter: true,
        handleSort: () => handleSort('created_by'),
        width: '230px',
        render: (record: ILesson) => (
          <div className="flex items-center gap-2">
            <UserAvatarList
              user={record.created_by?.name || ''}
              image={getAssetUrl(record.created_by?.profile_pic)}
              className="w-10 h-10 max-w-10"
            />
            <span className="text-neutral-primary text-sm truncate">
              {record.created_by?.name || '-'}
            </span>
          </div>
        ),
      },
      {
        key: 'class_name',
        title: 'lessons.columns.class-assigned',
        sorter: true,
        handleSort: () => handleSort('class_name'),
        width: '160px',
        render: (record: ILesson) => (
          <span className="text-primary-600 font-semibold text-sm truncate">
            {record.class_name}
          </span>
        ),
      },
      {
        key: 'assigned_date',
        title: 'lessons.columns.assigned-date',
        sorter: true,
        handleSort: () => handleSort('assigned_date'),
        width: '150px',
        render: (record: ILesson) => (
          <span className="text-neutral-secondary text-sm whitespace-nowrap">
            {record.assigned_date
              ? formatUnixToLongDate(record.assigned_date)
              : '-'}
          </span>
        ),
      },
      {
        key: 'due_date',
        title: 'lessons.columns.due-date',
        sorter: true,
        handleSort: () => handleSort('due_date'),
        width: '150px',
        render: (record: ILesson) => (
          <span className="text-neutral-secondary text-sm whitespace-nowrap">
            {record.due_date ? formatUnixToLongDate(record.due_date) : '-'}
          </span>
        ),
      },
      // {
      //   key: 'stage',
      //   title: 'lessons.columns.level',
      //   sorter: true,
      //   handleSort: () => handleSort('stage'),
      //   width: '160px',
      //   render: (row: ILesson) => {
      //     const levelConfig = row.stage
      //       ? LessonStageConfig[row.stage]
      //       : undefined;

      //     return (
      //       <span
      //         className={`px-2 py-1 w-fit rounded-full text-xs font-semibold whitespace-nowrap ${
      //           levelConfig?.className ?? ''
      //         }`}
      //       >
      //         {levelConfig?.label ? formatText(levelConfig.label) : row?.stage}
      //       </span>
      //     );
      //   },
      // },
      {
        key: 'active_students',
        title: 'lessons.columns.students',
        sorter: true,
        handleSort: () => handleSort('active_students'),
        align: 'start' as const,
        width: '150px',
        render: (record: ILesson) => (
          <span className="text-success-primary font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-green-50 w-fit">
            {record.active_students}
          </span>
        ),
      },

      {
        key: 'avg_completion',
        title: 'lessons.columns.avg-completion',
        sorter: true,
        handleSort: () => handleSort('avg_completion'),
        width: '180px',
        render: (record: ILesson) => (
          <ProgressBar value={record.avg_completion || 0} />
        ),
      },
      {
        key: 'status',
        title: 'lessons.columns.status',
        sorter: true,
        handleSort: () => handleSort('status'),
        width: '140px',
        render: (record: ILesson) => {
          const config =
            LessonStatusConfig[
              record.status as keyof typeof LessonStatusConfig
            ];

          return (
            <TagCapsule
              value={formatText(config?.label ?? '')}
              className={config?.className ?? ''}
            />
          );
        },
      },

      {
        key: 'last_update',
        title: 'lessons.columns.last-update',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        width: '120px',
        render: (record: ILesson) => (
          <span className="text-neutral-secondary text-sm whitespace-nowrap">
            {record.last_update
              ? formatLastActive(record.last_update, formatText)
              : '-'}
          </span>
        ),
      },
      {
        key: 'action',
        title: 'lessons.columns.action',
        align: 'start' as const,
        width: '70px',
        render: (record: ILesson) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => {
                  navigate(`/school/lessons/${record.id}`);
                },
              },
              {
                key: 'block',
                label:
                  record.status === ELessonStatus.IN_ACTIVE
                    ? ACTIONS_UNBLOCK
                    : ACTIONS_BLOCK,
                icon: <IconBan size={18} />,
                isDanger: record.status !== ELessonStatus.IN_ACTIVE,
                onClick: () => setBlockModal({ open: true, lesson: record }),
              },
              {
                key: 'remove',
                label: 'common.actions.remove',
                icon: <IconTrash size={18} />,
                isDanger: true,
                onClick: () => setConfirmModal({ open: true, lesson: record }),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortField, sortDir, formatText]
  );

  const hasActiveFilters = useMemo(
    () =>
      !!filters &&
      Object.values(filters).some((v) =>
        Array.isArray(v) ? v.length > 0 : !!v
      ),
    [filters]
  );
  const hasNoLessons =
    !isScreenLoading &&
    lessons.length === 0 &&
    !debouncedSearch &&
    !hasActiveFilters;
  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    lessons.length === 0 &&
    (!!debouncedSearch || hasActiveFilters);

  if (isScreenLoading) {
    return <ListSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="lessons-page-container">
      {/* Search & Actions */}
      {!hasNoLessons && (
        <div
          className="flex items-center justify-between gap-4 pl-1"
          data-testid="lessons-filters"
        >
          <div className="flex items-center gap-4 flex-1">
            <Input
              name="search"
              register={register}
              placeholder={formatText('lessons.search-placeholder')}
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="max-w-70"
              inputClassName="py-3"
              dataTestId="lesson-search-input"
            />
            <div className="relative">
              <Button
                variant="outline"
                label={formatText('lessons.filter')}
                icon={<IconAdjustments size={20} />}
                className={clsx(
                  'h-12',
                  isFilterApplied && 'border-brand-default! text-brand-default!'
                )}
                onClick={() => setIsFilterOpen(true)}
                dataTestId="lesson-filter-button"
              />
              {isFilterApplied && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <CustomTable
        dataSource={lessons}
        columns={columns}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isLoading={isTableLoading}
        isEmptySearch={isEmptySearch}
        fieldName={sortField}
        sortDirection={sortDir}
        pageSize={10}
        isEmpty={hasNoLessons}
        showHeader={!hasNoLessons}
        hidePagination={hasNoLessons}
        emptyTitle={formatText('lessons.empty-title')}
        emptyDescription={formatText('lessons.school-empty-description')}
        dataTestId="lessons-table"
        onRowClick={(id: string) => {
          navigate(`/school/lessons/${id}`);
        }}
      />

      {/* Block / Unblock Confirmation Modal */}
      <Activity mode={blockModal.open ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={blockModal.open}
          setIsOpen={(open) =>
            setBlockModal({ open, lesson: open ? blockModal.lesson : null })
          }
          title={
            blockModal.lesson?.status === ELessonStatus.IN_ACTIVE
              ? 'lessons.unblock-modal.title'
              : 'lessons.block-modal.title'
          }
          descriptionPrefix={
            blockModal.lesson?.status === ELessonStatus.IN_ACTIVE
              ? 'common.confirmation.unblock-prefix'
              : 'common.confirmation.block-prefix'
          }
          descriptionSuffix={
            blockModal.lesson?.status === ELessonStatus.IN_ACTIVE
              ? 'lessons.unblock-modal.suffix'
              : 'lessons.block-modal.suffix'
          }
          entityName={blockModal.lesson?.lesson_name || ''}
          confirmLabel={formatText(
            blockModal.lesson?.status === ELessonStatus.IN_ACTIVE
              ? ACTIONS_UNBLOCK
              : ACTIONS_BLOCK
          )}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={handleBlockLesson}
          isDestructive={blockModal.lesson?.status !== ELessonStatus.IN_ACTIVE}
          dataTestId="block-lesson-modal"
        />
      </Activity>

      {/* Remove Confirmation Modal */}
      <Activity mode={confirmModal.open ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={confirmModal.open}
          setIsOpen={(open) =>
            setConfirmModal({ open, lesson: open ? confirmModal.lesson : null })
          }
          title={'lessons.remove-modal.title'}
          descriptionPrefix={'lessons.remove-modal.description'}
          descriptionSuffix={'lessons.remove-modal.suffix'}
          confirmLabel={formatText('common.actions.remove')}
          entityName={confirmModal?.lesson?.lesson_name || ''}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={handleRemove}
          isDestructive
          dataTestId="remove-lesson-modal"
        />
      </Activity>

      {/* Filter Drawer */}
      <Activity mode={isFilterOpen ? 'visible' : 'hidden'}>
        <FilterDrawer
          isOpen={isFilterOpen}
          setIsOpen={setIsFilterOpen}
          onApplyFilters={handleApplyFilters}
        />
      </Activity>
    </div>
  );
};

export default Lessons;
